/* =========================================================
   FILE: cn-membership-landing.js
   Billing, Mobile Cards, Table Fades, Cart Guard und FAQ.
========================================================= */

(() => {
  'use strict';

  const initialized = new WeakSet();

  const initialize = (section) => {
    if (!section || initialized.has(section)) return;
    initialized.add(section);


    const tableScrollers = Array.from(
      section.querySelectorAll('[data-cn-membership-table-scroll]')
    );

    const getHorizontalScrollState = (scroller) => {
      const maxScrollLeft = Math.max(
        0,
        scroller.scrollWidth - scroller.clientWidth
      );

      const currentScrollLeft = Math.max(0, scroller.scrollLeft);
      const tolerance = 3;
      const hasOverflow = maxScrollLeft > tolerance;

      return {
        hasOverflow,
        isAtStart: !hasOverflow || currentScrollLeft <= tolerance,
        isAtEnd:
          !hasOverflow ||
          currentScrollLeft >= maxScrollLeft - tolerance
      };
    };

    const updateTableScrollEdges = (scroller) => {
      if (!scroller) return;

      const state = getHorizontalScrollState(scroller);

      scroller.classList.toggle('has-overflow', state.hasOverflow);
      scroller.classList.toggle('is-at-start', state.isAtStart);
      scroller.classList.toggle('is-at-end', state.isAtEnd);
    };

    tableScrollers.forEach((scroller) => {
      let scrollFrame = 0;

      const requestTableEdgeUpdate = () => {
        if (scrollFrame) return;

        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          updateTableScrollEdges(scroller);
        });
      };

      scroller.addEventListener('scroll', requestTableEdgeUpdate, {
        passive: true
      });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(requestTableEdgeUpdate);

        resizeObserver.observe(scroller);

        const table = scroller.querySelector('table');
        if (table) resizeObserver.observe(table);
      }

      updateTableScrollEdges(scroller);

      window.requestAnimationFrame(() => {
        updateTableScrollEdges(scroller);
      });
    });

    if (tableScrollers.length) {
      let tableResizeFrame = 0;

      window.addEventListener('resize', () => {
        if (tableResizeFrame) return;

        tableResizeFrame = window.requestAnimationFrame(() => {
          tableResizeFrame = 0;
          tableScrollers.forEach(updateTableScrollEdges);
        });
      }, { passive: true });
    }

    const plansScroller = section.querySelector('.cn-membership__plans');

    const mobilePlansQuery = window.matchMedia('(max-width: 1199px)');

    const getPlansScrollState = () => {
      if (!plansScroller) {
        return {
          maxScrollLeft: 0,
          currentScrollLeft: 0,
          hasOverflow: false,
          isAtStart: true,
          isAtEnd: true
        };
      }

      const maxScrollLeft = Math.max(
        0,
        plansScroller.scrollWidth - plansScroller.clientWidth
      );
      const currentScrollLeft = Math.max(0, plansScroller.scrollLeft);
      const tolerance = 3;
      const hasOverflow = maxScrollLeft > tolerance;

      return {
        maxScrollLeft,
        currentScrollLeft,
        hasOverflow,
        isAtStart: !hasOverflow || currentScrollLeft <= tolerance,
        isAtEnd:
          !hasOverflow ||
          currentScrollLeft >= maxScrollLeft - tolerance
      };
    };

    const updatePlansScrollEdges = () => {
      if (!plansScroller) return;

      const state = getPlansScrollState();

      plansScroller.classList.toggle('has-overflow', state.hasOverflow);
      plansScroller.classList.toggle('is-at-start', state.isAtStart);
      plansScroller.classList.toggle('is-at-end', state.isAtEnd);
    };

    const centerPlanCard = (card, behavior = 'auto') => {
      if (!plansScroller || !card || !mobilePlansQuery.matches) return;

      const targetScrollLeft =
        card.offsetLeft -
        ((plansScroller.clientWidth - card.offsetWidth) / 2);

      plansScroller.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior
      });
    };

    let mobilePlansInitialized = false;
    let previousMobilePlansMatch = mobilePlansQuery.matches;

    const initializeMobilePlans = ({ centerPlus = false } = {}) => {
      if (!plansScroller) return;

      if (!mobilePlansQuery.matches) {
        mobilePlansInitialized = false;
        plansScroller.classList.remove('is-mobile-initialized');
        updatePlansScrollEdges();
        return;
      }

      /*
        Plus nur beim ersten echten Initialisieren der horizontalen Kartenansicht zentrieren.
        Mobile Browser lösen beim vertikalen Scrollen häufig resize-Events aus,
        weil sich die Browserleiste ein- oder ausblendet. Dabei darf die aktuell
        gewählte Karte nicht erneut auf Plus zurückgesetzt werden.
      */
      if (centerPlus && !mobilePlansInitialized) {
        const plusCard = plansScroller.querySelector(
          '.cn-membership-card:nth-child(2)'
        );

        centerPlanCard(plusCard, 'auto');
      }

      updatePlansScrollEdges();
      plansScroller.classList.add('is-mobile-initialized');
      mobilePlansInitialized = true;
    };

    if (plansScroller) {
      let plansScrollFrame = 0;
      let plansResizeTimer = 0;

      const requestPlansEdgeUpdate = () => {
        if (plansScrollFrame) return;

        plansScrollFrame = window.requestAnimationFrame(() => {
          plansScrollFrame = 0;
          updatePlansScrollEdges();
        });
      };

      const handlePlansResize = () => {
        window.clearTimeout(plansResizeTimer);

        plansResizeTimer = window.setTimeout(() => {
          const isMobileNow = mobilePlansQuery.matches;
          const enteredMobileLayout = isMobileNow && !previousMobilePlansMatch;

          previousMobilePlansMatch = isMobileNow;
          initializeMobilePlans({ centerPlus: enteredMobileLayout });
        }, 80);
      };

      plansScroller.addEventListener('scroll', requestPlansEdgeUpdate, {
        passive: true
      });

      window.addEventListener('resize', handlePlansResize, {
        passive: true
      });

      if ('ResizeObserver' in window) {
        const plansResizeObserver = new ResizeObserver(requestPlansEdgeUpdate);
        plansResizeObserver.observe(plansScroller);

        plansScroller
          .querySelectorAll('.cn-membership-card')
          .forEach((card) => plansResizeObserver.observe(card));
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          initializeMobilePlans({ centerPlus: true });
        });
      });
    }

    const shopifyRoot =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    const cartRoutes = {
      cart: `${shopifyRoot}cart.js`,
      add: `${shopifyRoot}cart/add.js`,
      update: `${shopifyRoot}cart/update.js`,
      cartPage: section.dataset.cartUrl || `${shopifyRoot}cart`
    };

    const membershipVariantIds = new Set(
      [
        section.dataset.plusVariantId,
        section.dataset.proVariantId,
        section.dataset.primeVariantId
      ].filter(Boolean).map(String)
    );

    let currentBilling = 'monthly';

    const billingRoot = section.querySelector('[data-cn-membership-billing]');
    const billingButtons = section.querySelectorAll('[data-billing]');

    const updateFormForBilling = (form, billing) => {
      const variantInput = form.querySelector('.cn-membership-variant-input');
      const sellingPlanInput = form.querySelector('.cn-membership-selling-plan-input');
      const billingPropertyInput = form.querySelector('.cn-membership-billing-property-input');
      const submitButton = form.querySelector('button[type="submit"]');

      const variantId =
        billing === 'yearly'
          ? variantInput?.dataset.variantYearly
          : variantInput?.dataset.variantMonthly;

      const sellingPlanId =
        billing === 'yearly'
          ? sellingPlanInput?.dataset.sellingPlanYearly
          : sellingPlanInput?.dataset.sellingPlanMonthly;

      if (variantInput) {
        variantInput.value = variantId || '';
      }

      if (sellingPlanInput) {
        sellingPlanInput.value = sellingPlanId || '';
      }

      if (billingPropertyInput) {
        billingPropertyInput.value = billing;
      }

      if (submitButton) {
        if (!submitButton.dataset.defaultText) {
          submitButton.dataset.defaultText = submitButton.textContent.trim();
        }

        if (!variantId) {
          submitButton.disabled = true;
          submitButton.textContent = billing === 'yearly' ? 'Jahresprodukt fehlt' : 'Monatsprodukt fehlt';
        } else if (!sellingPlanId) {
          submitButton.disabled = true;
          submitButton.textContent = 'Selling Plan fehlt';
        } else {
          submitButton.disabled = false;
          submitButton.textContent = submitButton.dataset.defaultText;
        }
      }

      return {
        variantId: variantId || '',
        sellingPlanId: sellingPlanId || ''
      };
    };

    const applyBilling = (billing) => {
      currentBilling = billing === 'yearly' ? 'yearly' : 'monthly';

      if (billingRoot) {
        billingRoot.dataset.activeBilling = currentBilling;
        billingRoot.setAttribute('aria-checked', currentBilling === 'yearly' ? 'true' : 'false');
      }

      billingButtons.forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.billing === currentBilling);
      });

      section.querySelectorAll('[data-price-monthly][data-price-yearly]').forEach((priceEl) => {
        const value = currentBilling === 'yearly'
          ? priceEl.dataset.priceYearly
          : priceEl.dataset.priceMonthly;

        if (value) priceEl.textContent = value;
      });

      section.querySelectorAll('[data-period-monthly][data-period-yearly]').forEach((periodEl) => {
        const value = currentBilling === 'yearly'
          ? periodEl.dataset.periodYearly
          : periodEl.dataset.periodMonthly;

        if (value) periodEl.textContent = value;
      });

      section.querySelectorAll('[data-cn-membership-form]').forEach((form) => {
        updateFormForBilling(form, currentBilling);
      });
    };

    let suppressBillingClick = false;

    billingButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        if (suppressBillingClick) {
          event.preventDefault();
          return;
        }

        applyBilling(button.dataset.billing);
      });
    });

    if (billingRoot) {
      billingRoot.addEventListener('click', (event) => {
        if (suppressBillingClick) {
          event.preventDefault();
          return;
        }

        if (event.target.closest('[data-billing]')) return;

        applyBilling(currentBilling === 'yearly' ? 'monthly' : 'yearly');
      });

      billingRoot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();

        if (suppressBillingClick) return;

        applyBilling(currentBilling === 'yearly' ? 'monthly' : 'yearly');
      });
    }

    if (billingRoot) {
      let dragPointerId = null;
      let dragStartX = 0;
      let dragStartProgress = 0;
      let dragProgress = 0;
      let didDrag = false;
      let hasPointerCapture = false;

      const getBillingProgress = () => {
        return billingRoot.dataset.activeBilling === 'yearly' ? 1 : 0;
      };

      const setDragVisual = (progress) => {
        dragProgress = Math.min(1, Math.max(0, progress));
        billingRoot.dataset.dragPosition = 'true';

        billingRoot.style.setProperty(
          '--cn-billing-drag-x',
          `${dragProgress * 100}%`
        );
      };

      const clearDragVisual = () => {
        billingRoot.classList.remove('is-dragging');
        billingRoot.removeAttribute('data-drag-position');
        billingRoot.style.removeProperty('--cn-billing-drag-x');
      };

      const finishBillingDrag = (cancelled = false) => {
        if (dragPointerId === null) return;

        const pointerId = dragPointerId;
        dragPointerId = null;

        if (hasPointerCapture) {
          try {
            if (billingRoot.hasPointerCapture(pointerId)) {
              billingRoot.releasePointerCapture(pointerId);
            }
          } catch (error) {}
        }

        hasPointerCapture = false;
        clearDragVisual();

        if (!cancelled && didDrag) {
          applyBilling(dragProgress >= 0.5 ? 'yearly' : 'monthly');

          suppressBillingClick = true;
          window.setTimeout(() => {
            suppressBillingClick = false;
          }, 100);
        }

        didDrag = false;
      };

      billingRoot.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;

        dragPointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartProgress = getBillingProgress();
        dragProgress = dragStartProgress;
        didDrag = false;
        hasPointerCapture = false;
      });

      billingRoot.addEventListener(
        'pointermove',
        (event) => {
          if (dragPointerId === null || event.pointerId !== dragPointerId) {
            return;
          }

          const rect = billingRoot.getBoundingClientRect();
          const travel = Math.max(1, rect.width / 2);
          const deltaX = event.clientX - dragStartX;

          if (!didDrag && Math.abs(deltaX) > 6) {
            didDrag = true;
            billingRoot.classList.add('is-dragging');

            try {
              billingRoot.setPointerCapture(event.pointerId);
              hasPointerCapture = true;
            } catch (error) {}
          }

          if (!didDrag) return;

          event.preventDefault();
          setDragVisual(dragStartProgress + deltaX / travel);
        },
        { passive: false }
      );

      billingRoot.addEventListener('pointerup', () => {
        finishBillingDrag(false);
      });

      billingRoot.addEventListener('pointercancel', () => {
        finishBillingDrag(true);
      });

      billingRoot.addEventListener('lostpointercapture', () => {
        if (dragPointerId !== null && hasPointerCapture) {
          finishBillingDrag(false);
        }
      });
    }

    const isMembershipCartItem = (item) => {
      const properties = item.properties || {};

      const hasMembershipProperty =
        properties._cn_membership === 'true' ||
        properties._cn_membership === true ||
        properties._cn_membership === '1';

      const isKnownMembershipVariant =
        item.variant_id && membershipVariantIds.has(String(item.variant_id));

      const hasSellingPlan = Boolean(item.selling_plan_allocation);

      return hasMembershipProperty || (hasSellingPlan && isKnownMembershipVariant);
    };

    const fetchJson = async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          Accept: 'application/json',
          ...(options.headers || {})
        }
      });

      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok) {
        const message =
          data?.description ||
          data?.message ||
          'Die Mitgliedschaft konnte nicht aktualisiert werden.';

        throw new Error(message);
      }

      return data;
    };

    const removeExistingMemberships = async () => {
      const cart = await fetchJson(cartRoutes.cart);
      const updates = {};

      cart.items.forEach((item) => {
        if (isMembershipCartItem(item)) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return;

      await fetchJson(cartRoutes.update, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
    };

    section.querySelectorAll('[data-cn-membership-form]').forEach((form) => {
      const submitButton = form.querySelector('button[type="submit"]');

      if (submitButton) {
        submitButton.dataset.defaultText = submitButton.textContent.trim();
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const formState = updateFormForBilling(form, currentBilling);
        const originalButtonText = submitButton ? submitButton.dataset.defaultText : '';

        try {
          if (!formState.variantId) {
            throw new Error(currentBilling === 'yearly' ? 'Für diesen Tarif ist kein Jahresprodukt hinterlegt.' : 'Für diesen Tarif ist kein Monatsprodukt hinterlegt.');
          }

          if (!formState.sellingPlanId) {
            throw new Error('Für diesen Tarif fehlt die Selling Plan ID.');
          }

          if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Wird aktualisiert...';
          }

          const formData = new FormData(form);

          formData.set('id', formState.variantId);
          formData.set('selling_plan', formState.sellingPlanId);
          formData.set('quantity', '1');
          formData.set('properties[_cn_membership]', 'true');
          formData.set('properties[_cn_membership_billing]', currentBilling);

          await removeExistingMemberships();

          await fetchJson(cartRoutes.add, {
            method: 'POST',
            body: formData
          });

          window.location.href = cartRoutes.cartPage;
        } catch (error) {
          console.error(error);

          alert(error.message || 'Die Mitgliedschaft konnte nicht aktualisiert werden.');

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
          }

          updateFormForBilling(form, currentBilling);
        }
      });
    });

    const scrollToPerkDetail = (targetId) => {
      if (!targetId) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      const header =
        document.querySelector('.shopify-section-header-sticky') ||
        document.querySelector('.section-header') ||
        document.querySelector('header');

      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const extraOffset = 22;

      const targetTop =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        headerHeight -
        extraOffset;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    };

    section.querySelectorAll('.cn-membership-table__click-row[data-cn-scroll-to]').forEach((row) => {
      row.setAttribute('role', 'button');
      row.setAttribute('aria-label', 'Mehr Details zu diesem Vorteil anzeigen');

      row.addEventListener('click', () => {
        const targetId = row.dataset.cnScrollTo;
        scrollToPerkDetail(targetId);
      });

      row.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();

        const targetId = row.dataset.cnScrollTo;
        scrollToPerkDetail(targetId);
      });
    });

    const faqDetails = section.querySelectorAll('.cn-membership-faq details');

    faqDetails.forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;

        faqDetails.forEach((otherDetail) => {
          if (otherDetail !== detail) {
            otherDetail.removeAttribute('open');
          }
        });
      });
    });

    applyBilling(currentBilling);
  
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-cn-membership-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-cn-membership-root]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
